/**
 * DocRowTruncationChip — Documents tab chip for uploads whose extracted text
 * was cut at the platform cap.
 *
 * The cap (`EXTRACTED_TEXT_MAX_CHARS`, 10,000 chars ≈ 3–4 pages of prose)
 * applies to `textContent`, the SED copy, the RAG chunks and every report
 * prompt, so a long report's later pages are invisible to the platform.
 * Until this chip nothing told the user: the Pineywoods Park wetlands
 * delineation (71 pages) lost its Conclusions section silently on
 * 2026-09-07. Rendered next to the extraction-status chip in both the row
 * and the card views; renders nothing for documents that were not cut or
 * that predate the `extractedCharsTotal` stamp.
 */
import React from 'react';
import type { SiteDocument } from '../../../types/siteRag';
import { isExtractionTruncated, extractedCharsKept } from '../../../lib/docExtractionLimits';

export function truncationTooltip(doc: Pick<SiteDocument, 'extractedCharsTotal' | 'extractedCharsKept' | 'pdfCoverage'>): string {
  const total = doc.extractedCharsTotal ?? 0;
  const kept = extractedCharsKept(doc);
  if (doc.pdfCoverage && doc.pdfCoverage.totalPages > doc.pdfCoverage.textPagesRead) {
    return `Text reading reached ${doc.pdfCoverage.textPagesRead} of ${doc.pdfCoverage.totalPages} PDF pages. Split the source and upload the remaining pages; later content is not available to reports.`;
  }
  const keptPct = total > 0
    ? Math.max(1, Math.round((kept / total) * 100))
    : 100;
  return (
    `Only the first ${kept.toLocaleString('en-US')} of `
    + `${total.toLocaleString('en-US')} extracted characters (about ${keptPct}%) were captured `
    + 'for evidence extraction, retrieval and reports. Anything after that point is '
    + 'invisible to the platform. Re-upload the document to capture the whole body, '
    + 'or upload a short summary of the rest.'
  );
}

interface Props {
  doc: SiteDocument;
}

export const DocRowTruncationChip: React.FC<Props> = ({ doc }) => {
  if (!isExtractionTruncated(doc)) return null;
  const tooltip = truncationTooltip(doc);
  return (
    <span
      data-testid={`doc-row-${doc.id}-truncated`}
      data-total-chars={doc.extractedCharsTotal}
      title={tooltip}
      aria-label={`Extraction truncated. ${tooltip}`}
      className="da-status da-status--cond shrink-0"
    >
      <span className="da-status__dot" aria-hidden />
      TRUNCATED
    </span>
  );
};
